import { Fragment, useEffect, useRef } from 'react';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import type { Options } from '@splidejs/splide';
import '@splidejs/react-splide/css';
import { useMediaQuery } from '@/hooks';
import { responsiveBreakpointsPx } from '@/constants';
import { Button } from '@/ui-components/atoms/Button/Button';
import styles from './channelHeader.module.scss';

const { channelSelectorWrapper, buttonStyles } = styles;

export type ChannelButton = { title: string };

interface ChannelHeaderProps {
    channelButtons: ChannelButton[];
    value: string;
    onClick: (arg: string) => void;
}

export default function ChannelHeader({ channelButtons, value, onClick }: ChannelHeaderProps) {
    const viewportIsMd = useMediaQuery(`(min-width: ${responsiveBreakpointsPx.md}px)`);
    const splideRef = useRef<Splide | null>(null);
    const activeButton = Math.max(
        channelButtons.findIndex((button) => button.title === value),
        0,
    );

    useEffect(() => {
        splideRef.current?.splide?.go(activeButton);
    }, [activeButton]);

    function handleButtonClick(index: number) {
        splideRef.current?.splide?.go(index);
        onClick(channelButtons[index].title);
    }

    const channelTabButtons = channelButtons.map((button, index) => {
        return {
            content: (
                <Button
                    key={index}
                    onClick={() => handleButtonClick(index)}
                    variant="custom"
                    color={index === activeButton ? 'var(--base-contrast, #1E2226)' : 'var(--base-surface, #F3F5F7)'}
                    disableHoverGrow
                    type="button"
                    className={buttonStyles}
                >
                    {button.title}
                </Button>
            ),
        };
    });

    if (viewportIsMd) {
        return (
            <div className={channelSelectorWrapper}>
                {channelTabButtons.map((button, index) => (
                    <Fragment key={index}>{button.content}</Fragment>
                ))}
            </div>
        );
    } else {
        const sliderOptions: Options = {
            rewind: false,
            gap: '8px',
            pagination: false,
            arrows: false,
            drag: 'free',
            autoWidth: true,
            padding: '16px',
            focusableNodes: 'button',
        };

        return (
            <Splide options={sliderOptions} hasTrack={false} ref={splideRef}>
                <SplideTrack>
                    {channelTabButtons.map((slide, index) => {
                        return <SplideSlide key={index}>{slide.content}</SplideSlide>;
                    })}
                </SplideTrack>
            </Splide>
        );
    }
}
